import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import cx from "classnames";
import style from "./PainScaleSlider.module.scss";
import { blockBoundary, getPainColor, PAIN_SCALE, thumbCenter } from "../debridementConfig";

const PainScaleSlider = ({ id, name, defaultValue = "", disabled = false, classes = {}, onChange, validator, errors, painScale = PAIN_SCALE }) => {
  const hasInitialValue = defaultValue !== "" && defaultValue != null;
  const [numericValue, setNumericValue] = useState(hasInitialValue ? Number(defaultValue) : 0);
  const [selected, setSelected] = useState(hasInitialValue);

  const hiddenInputRef = useRef(null);
  const maxIdx = painScale.length - 1;

  useEffect(() => {
    const has = defaultValue !== "" && defaultValue != null;
    setSelected(has);
    setNumericValue(has ? Number(defaultValue) : 0);
  }, [defaultValue]);

  const colors = useMemo(() => painScale.map((_, idx) => getPainColor(idx, painScale.length)), [painScale]);

  const gradientCss = useMemo(() => {
    const stops = [];
    colors.forEach((c, i) => {
      const start = i === 0 ? "0%" : blockBoundary(i - 1, maxIdx);
      const end = i === maxIdx ? "100%" : blockBoundary(i, maxIdx);
      stops.push(`${c} ${start}`, `${c} ${end}`);
    });
    return `linear-gradient(to right, ${stops.join(", ")})`;
  }, [colors, maxIdx]);

  const labelPositions = useMemo(() => painScale.map((_, idx) => thumbCenter(idx, maxIdx)), [painScale, maxIdx]);

  const syncHiddenInput = useCallback((key) => {
    const input = hiddenInputRef.current;
    if (!input) return;
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
    nativeSetter.call(input, key);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("blur", { bubbles: true }));
  }, []);

  const commit = useCallback(
    (idx) => {
      const clamped = Math.min(Math.max(idx, 0), maxIdx);
      const key = painScale[clamped]?.key;
      if (key == null) return;
      setNumericValue(clamped);
      setSelected(true);
      syncHiddenInput(key);
      onChange?.({ target: { name, value: key } });
    },
    [painScale, maxIdx, name, onChange, syncHiddenInput]
  );

  const handleInput = (e) => {
    if (disabled) return;
    commit(Math.round(Number(e.target.value)));
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    let next = numericValue;
    if (e.key === "ArrowRight" || e.key === "ArrowUp") next = numericValue + 1;
    else if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = numericValue - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = maxIdx;
    else return;
    e.preventDefault();
    commit(next);
  };

  const selectedIdx = selected ? numericValue : -1;
  const thumbColor = selected ? colors[numericValue] : "transparent";
  const hasError = Boolean(errors && name && errors[name]);

  return (
    <div id={id} className={cx(style.pain_slider_column, classes.column)}>
      <div className={cx(style.pain_slider_track, classes.inputClassName, disabled && style.disabled, hasError && style.has_error)}>
        <div className={style.pain_slider_gradient} style={{ background: gradientCss }} />

        <input
          type="range"
          min={0}
          max={maxIdx}
          step={1}
          value={numericValue}
          disabled={disabled}
          onInput={handleInput}
          onChange={handleInput}
          onMouseDown={() => !disabled && setSelected(true)}
          onTouchStart={() => !disabled && setSelected(true)}
          onKeyDown={handleKeyDown}
          className={cx(style.pain_slider_range, !selected && style.range_unselected)}
          style={{ "--thumb-color": thumbColor }}
          aria-valuetext={selected ? painScale[numericValue].value : "No value selected"}
        />
      </div>

      <div className={style.pain_slider_labels}>
        {painScale.map((o, idx) => (
          <span key={o.key} className={cx(style.pain_slider_label, idx === selectedIdx && style.selected)} style={{ left: labelPositions[idx] }}>
            {idx}
          </span>
        ))}
      </div>

      <input
        type="hidden"
        name={name}
        readOnly
        value={selected ? painScale[numericValue].key : ""}
        ref={(el) => {
          hiddenInputRef.current = el;
          if (typeof validator === "function") validator(el);
          else if (validator && "current" in validator) validator.current = el;
        }}
      />

      {hasError && <span className={cx(style.error_message, classes.errorClassName)}>{errors[name].message}</span>}
    </div>
  );
};

export default PainScaleSlider;
